import { useState } from "react";
import SectionTitle from "./ui/SectionTitle";
import StoryCard from "./ui/StoryCard";
import { StoryCardSkeleton } from "../SkeletonLoaders";
import { useInfiniteCategoryNaray8Stories } from "../../hooks/api/useNaray8";
import { GENRE_MAP } from "./Upload/constants";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { EmptyStoryCategory } from "../EmptyUI";
import ErrorComponent from "../ErrorComponent";
import { formatNumber } from "../../helper";

const ExcludedCategoryMap = {
  "trending": "Trending / Popular Stories",
  "recommended": "Recommended Stories",
  "completed": "Completed Stories"
}

export default function CategorySection({ categorySlug, status, searchQuery }) {
  const [filter, setFilter] = useState(searchQuery || "hot");
  const slug = GENRE_MAP[categorySlug] ?? categorySlug;
  const isSearch = categorySlug === "search" && searchQuery
  const headerTitle = isSearch ? searchQuery : (ExcludedCategoryMap[categorySlug] || GENRE_MAP[categorySlug] || categorySlug || "All Stories");
  const { data, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } = useInfiniteCategoryNaray8Stories({ slug, filter, status });
  const stories = data ? data.pages.flatMap(page => page.data) : [];
  const totalItems = data ? data.pages?.[0]?.pagination?.totalItems : null
  const searchCount = totalItems ? formatNumber(totalItems) : ""
  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: hasNextPage,
    getNextPage: fetchNextPage,
  });
  const slugIsCategory = GENRE_MAP[categorySlug] !== undefined


  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-[11px]">
        <div className="col-span-full">
          <SectionTitle
            largeTitle={isSearch ? "Search" : slugIsCategory ? "Category" : headerTitle}
            title={slugIsCategory || isSearch ?
              <>
                {headerTitle} <span className="text-[14px]">{searchCount || ""}</span>
              </>
              : (searchCount || undefined)
            }
          />
        </div>
        {Array.from({ length: 5 }).map((_, index) => (
          <StoryCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (error && (stories.length === 0 || !data)) {
    return (
      <div className="col-span-full py-10">
        <ErrorComponent
          message="Failed to load stories. Please try again."
          onRetry={() => refetch()}
        />
      </div>
    );
  }


  return (
    <section
      className=""
    >
      <SectionTitle
        largeTitle={isSearch ? "Search" : slugIsCategory ? "Category" : headerTitle}
        title={slugIsCategory || isSearch ?
          <>
            {headerTitle} <span className="text-[14px]">{searchCount}</span>
          </>
          : searchCount
        }
        // rightElement should be a dropdown to select filter
        rightElement={(categorySlug === "search" || searchQuery) ? undefined :
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-customDark shadow-black/25 shadow-sm text-white px-3 py-2 rounded accent-customGold"
          >
            {[{ value: "hot", label: "Hot" }, { value: "latest", label: "Latest" }, { value: "random", label: "Random" }].map(option => (
              <option key={option.value} value={option.value}>{option.value === filter ? `Sort by: ${option.label}` : option.label}</option>
            ))}
          </select>
        }
      />

      {stories?.length === 0 && !isLoading && !error && (
        <div className="col-span-full">
          <EmptyStoryCategory categoryName={headerTitle} />
        </div>
      )}

      {!isLoading && stories?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-[11px]">
          {stories.map((story) => (
            <StoryCard
              key={story.narrateId}
              narrate={story}
            />
          ))}

          {
            error && (
              <div className="col-span-full py-10">
                <ErrorComponent
                  message="Failed to load more stories. Please try again."
                  onRetry={() => refetch()}
                />
              </div>
            )
          }

          {hasNextPage && (
            isFetchingNextPage && Array.from({ length: 5 }).map((_, index) => (
              <StoryCardSkeleton key={index} />
            ))
          )}
          <div ref={sentinelRef} className="h-10" />
        </div>
      )}
    </section>
  );
}

